import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, tap, distinctUntilChanged } from 'rxjs/operators';
import { ThemeConfig, ThemeColors, BrandAssets } from '../interfaces/theme.interface';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private currentThemeSubject = new BehaviorSubject<ThemeConfig | null>(null);
    private isThemeLoadingSubject = new BehaviorSubject<boolean>(false);

    // Observables públicos
    currentTheme$ = this.currentThemeSubject.asObservable();
    isThemeLoading$ = this.isThemeLoadingSubject.asObservable();

    // Referencias DOM
    private themeStyleElement: HTMLStyleElement | null = null;
    private customCSSElement: HTMLStyleElement | null = null;

    constructor(@Inject(DOCUMENT) private document: Document) {
        this.initializeThemeElements();
    }

    /**
     * Inicializa los elementos de estilo en el DOM
     */
    private initializeThemeElements(): void {
        // Elemento para variables CSS del tema
        const existingThemeStyle = this.document.getElementById('dynamic-theme-variables') as HTMLStyleElement | null;
        this.themeStyleElement = existingThemeStyle ?? this.document.createElement('style');
        this.themeStyleElement.id = 'dynamic-theme-variables';
        if (!existingThemeStyle) {
            this.document.head.appendChild(this.themeStyleElement);
        }

        // Elemento para CSS personalizado
        const existingCustomCSS = this.document.getElementById('custom-theme-css') as HTMLStyleElement | null;
        this.customCSSElement = existingCustomCSS ?? this.document.createElement('style');
        this.customCSSElement.id = 'custom-theme-css';
        if (!existingCustomCSS) {
            this.document.head.appendChild(this.customCSSElement);
        }
    }

    /**
     * Aplica un tema completo
     */
    applyTheme(theme: ThemeConfig): void {
        this.isThemeLoadingSubject.next(true);

        try {
            // Aplicar variables CSS
            this.applyThemeVariables(theme);

            // Aplicar CSS personalizado si existe
            if (theme.customCSS) {
                this.applyCustomCSS(theme.customCSS);
            } else {
                this.clearCustomCSS();
            }

            // Aplicar assets (favicon, etc.)
            this.applyBrandAssets(theme.assets);

            // Actualizar el tema actual
            this.currentThemeSubject.next(theme);

            // Guardar tema en localStorage para persistencia
            this.saveThemeToStorage(theme);
        } catch (error) {
            console.error('Error aplicando tema:', error);
        } finally {
            this.isThemeLoadingSubject.next(false);
        }
    }

    /**
     * Aplica las variables CSS del tema
     */
    private applyThemeVariables(theme: ThemeConfig): void {
        if (!this.themeStyleElement) return;

        const cssVariables = this.generateCSSVariables(theme);
        this.themeStyleElement.textContent = cssVariables;
    }

    /**
     * Genera las variables CSS a partir del tema
     */
    private generateCSSVariables(theme: ThemeConfig): string {
        const { colors, typography, spacing, borderRadius, shadows } = theme;

        return `
      :root {
        /* Colores principales */
        --theme-primary: ${colors.primary};
        --theme-primary-light: ${colors.primaryLight || colors.primary};
        --theme-primary-dark: ${colors.primaryDark || colors.primary};
        ${colors.secondary ? `--theme-secondary: ${colors.secondary};` : ''} /* Solo si hay personalización */
        ${colors.secondaryLight ? `--theme-secondary-light: ${colors.secondaryLight};` : ''} /* Solo si hay personalización */
        ${colors.secondaryDark ? `--theme-secondary-dark: ${colors.secondaryDark};` : ''} /* Solo si hay personalización */
        
        /* Colores de estado */
        --theme-success: ${colors.success};
        --theme-warning: ${colors.warning};
        --theme-error: ${colors.error};
        --theme-info: ${colors.info};
        
        /* Colores de fondo */
        --theme-background: ${colors.background};
        --theme-background-secondary: ${colors.backgroundSecondary || colors.background};
        --theme-surface: ${colors.surface};
        --theme-surface-variant: ${colors.surfaceVariant || colors.surface};
        
        /* Colores de texto */
        ${colors.textPrimary ? `--theme-text-primary: ${colors.textPrimary};` : ''} /* Solo si hay personalización específica */
        --theme-text-secondary: ${colors.textSecondary};
        --theme-text-muted: ${colors.textMuted};
        --theme-text-on-primary: ${colors.textOnPrimary};
        --theme-text-on-secondary: ${colors.textOnSecondary};
        --theme-text-on-background: ${colors.textOnBackground};
        --theme-text-on-surface: ${colors.textOnSurface};
        
        /* Colores de elementos UI */
        --theme-border: ${colors.border};
        --theme-border-light: ${colors.borderLight || colors.border};
        --theme-divider: ${colors.divider};
        --theme-disabled: ${colors.disabled};
        --theme-hover: ${colors.hover};
        --theme-focus: ${colors.focus};
        --theme-active: ${colors.active};
        --theme-shadow: ${colors.shadow};
        --theme-overlay: ${colors.overlay || 'rgba(0, 0, 0, 0.5)'};
        
        /* Variables específicas de componentes - Solo se definen si hay personalización */
        ${colors.todayBackground ? `--theme-today-bg: ${colors.todayBackground};` : ''} /* Color de fondo para día actual en calendario */
        ${colors.linkColor ? `--theme-link-color: ${colors.linkColor};` : ''} /* Color para enlaces y texto de acción */
        ${colors.clientCountBg ? `--theme-client-count-bg: ${colors.clientCountBg};` : ''} /* Color de fondo para contador de clientes */
        ${colors.clientCountText ? `--theme-client-count-text: ${colors.clientCountText};` : ''} /* Color de texto para contador de clientes */
        ${colors.topbarBg ? `--theme-topbar-bg: ${colors.topbarBg};` : ''} /* Color de fondo para barra superior */
        ${colors.profileCardBg ? `--theme-profile-card-bg: ${colors.profileCardBg};` : ''} /* Color de fondo para tarjeta de perfil */
        ${colors.arrowButtonBg ? `--theme-arrow-button-bg: ${colors.arrowButtonBg};` : ''} /* Color de fondo para botón flecha */
        ${colors.arrowButtonHoverBg ? `--theme-arrow-button-hover-bg: ${colors.arrowButtonHoverBg};` : ''} /* Color de fondo para botón flecha en hover */
        ${colors.pendingOrdersBackgroundColor ? `--theme-pending-orders-bg: ${colors.pendingOrdersBackgroundColor};` : ''} /* Color de fondo para KPI de pedidos pendientes */
        ${colors.pendingOrdersBorder ? `--theme-pending-orders-border: ${colors.pendingOrdersBorder};` : ''} /* Configuración de borde para KPI de pedidos pendientes */
        ${colors.userAvatarUrl ? `--user-avatar-url: url('${colors.userAvatarUrl}');` : ''} /* Avatar personalizado del usuario */
        ${colors.chevronImageUrl ? `--chevron-image-url: url('${colors.chevronImageUrl}');` : ''} /* Chevron personalizado del topbar */
        ${colors.topbarLogoUrl ? `--topbar-logo-url: url('${colors.topbarLogoUrl}');` : ''} /* Logo personalizado del topbar */
        /* Tipografía */
        --theme-font-family: ${typography.fontFamily};
        --theme-font-size-base: ${typography.fontSizeBase};
        --theme-font-size-small: ${typography.fontSizeSmall};
        --theme-font-size-large: ${typography.fontSizeLarge};
        --theme-font-size-xlarge: ${typography.fontSizeXLarge};
        --theme-font-weight-light: ${typography.fontWeightLight};
        --theme-font-weight-normal: ${typography.fontWeightNormal};
        --theme-font-weight-medium: ${typography.fontWeightMedium};
        --theme-font-weight-bold: ${typography.fontWeightBold};
        --theme-line-height-base: ${typography.lineHeightBase};
        --theme-line-height-small: ${typography.lineHeightSmall};
        --theme-line-height-large: ${typography.lineHeightLarge};
        
        /* Espaciado */
        --theme-spacing-xs: ${spacing.xs};
        --theme-spacing-sm: ${spacing.sm};
        --theme-spacing-md: ${spacing.md};
        --theme-spacing-lg: ${spacing.lg};
        --theme-spacing-xl: ${spacing.xl};
        --theme-spacing-xxl: ${spacing.xxl};
        
        /* Border Radius */
        --theme-border-radius-none: ${borderRadius.none};
        --theme-border-radius-sm: ${borderRadius.sm};
        --theme-border-radius-md: ${borderRadius.md};
        --theme-border-radius-lg: ${borderRadius.lg};
        --theme-border-radius-xl: ${borderRadius.xl};
        --theme-border-radius-full: ${borderRadius.full};
        
        /* Sombras */
        --theme-shadow-none: ${shadows.none};
        --theme-shadow-sm: ${shadows.sm};
        --theme-shadow-md: ${shadows.md};
        --theme-shadow-lg: ${shadows.lg};
        --theme-shadow-xl: ${shadows.xl};
        
        /* Componentes - Botones */
        --theme-button-primary-bg: ${theme.components.buttons.primary.backgroundColor};
        --theme-button-primary-text: ${theme.components.buttons.primary.textColor};
        --theme-button-primary-border: ${theme.components.buttons.primary.borderColor};
        --theme-button-primary-border-radius: ${theme.components.buttons.primary.borderRadius};
        --theme-button-primary-padding: ${theme.components.buttons.primary.padding};
        --theme-button-primary-font-size: ${theme.components.buttons.primary.fontSize};
        --theme-button-primary-font-weight: ${theme.components.buttons.primary.fontWeight};
        --theme-button-primary-hover-bg: ${theme.components.buttons.primary.hoverBackgroundColor || theme.components.buttons.primary.backgroundColor};
        --theme-button-primary-hover-text: ${theme.components.buttons.primary.hoverTextColor || theme.components.buttons.primary.textColor};
        
        --theme-button-secondary-bg: ${theme.components.buttons.secondary.backgroundColor};
        --theme-button-secondary-text: ${theme.components.buttons.secondary.textColor};
        --theme-button-secondary-border: ${theme.components.buttons.secondary.borderColor};
        --theme-button-secondary-hover-bg: ${theme.components.buttons.secondary.hoverBackgroundColor || theme.components.buttons.secondary.backgroundColor};
        --theme-button-secondary-hover-text: ${theme.components.buttons.secondary.hoverTextColor || theme.components.buttons.secondary.textColor};
        
        /* Componentes - Inputs */
        --theme-input-bg: ${theme.components.inputs.backgroundColor};
        --theme-input-text: ${theme.components.inputs.textColor};
        --theme-input-border: ${theme.components.inputs.borderColor};
        --theme-input-border-radius: ${theme.components.inputs.borderRadius};
        --theme-input-padding: ${theme.components.inputs.padding};
        --theme-input-font-size: ${theme.components.inputs.fontSize};
        --theme-input-border-width: ${theme.components.inputs.borderWidth};
        --theme-input-focus-border: ${theme.components.inputs.focusBorderColor};
        --theme-input-placeholder: ${theme.components.inputs.placeholderColor};
        
        /* Componentes - Cards */
        --theme-card-bg: ${theme.components.cards.backgroundColor};
        --theme-card-border: ${theme.components.cards.borderColor};
        --theme-card-border-radius: ${theme.components.cards.borderRadius};
        --theme-card-padding: ${theme.components.cards.padding};
        --theme-card-shadow: ${theme.components.cards.shadow};
        --theme-card-header-bg: ${theme.components.cards.headerBackgroundColor || theme.components.cards.backgroundColor};
        --theme-card-header-text: ${theme.components.cards.headerTextColor || theme.components.cards.backgroundColor};
        
        /* Componentes - Navegación */
        --theme-navigation-bg: ${theme.components.navigation.backgroundColor};
        --theme-navigation-text: ${theme.components.navigation.textColor};
        --theme-navigation-active: ${theme.components.navigation.activeColor};
        --theme-navigation-hover: ${theme.components.navigation.hoverColor};
        --theme-navigation-border: ${theme.components.navigation.borderColor || 'transparent'};
        --theme-navigation-logo-height: ${theme.components.navigation.logoHeight || '40px'};
        --theme-navigation-menu-padding: ${theme.components.navigation.menuItemPadding || '12px 16px'};
        
        /* Componentes - Sidebar */
        --theme-sidebar-bg: ${theme.components.sidebar.backgroundColor};
        --theme-sidebar-text: ${theme.components.sidebar.textColor};
        --theme-sidebar-border: ${theme.components.sidebar.borderColor};
        --theme-sidebar-active-bg: ${theme.components.sidebar.activeBackgroundColor};
        --theme-sidebar-active-text: ${theme.components.sidebar.activeTextColor};
        --theme-sidebar-hover-bg: ${theme.components.sidebar.hoverBackgroundColor};
        --theme-sidebar-hover-text: ${theme.components.sidebar.hoverTextColor};
        --theme-sidebar-width: ${theme.components.sidebar.width || '280px'};
      }
    `;
    }

    /**
     * Aplica CSS personalizado
     */
    private applyCustomCSS(customCSS: string): void {
        if (!this.customCSSElement) return;
        this.customCSSElement.textContent = customCSS;
    }

    private clearCustomCSS(): void {
        if (!this.customCSSElement) return;
        this.customCSSElement.textContent = '';
    }

    /**
     * Aplica assets de marca (favicon, etc.)
     */
    private applyBrandAssets(assets: BrandAssets): void {
        // Cambiar favicon
        if (assets.favicon) {
            this.updateFavicon(assets.favicon);
        }

        // Aplicar imagen de fondo si existe
        if (assets.backgroundImage) {
            this.document.body.style.setProperty('--theme-bg-image', `url(${assets.backgroundImage})`);
        }

        // Aplicar logos como variables CSS
        if (assets.logo) {
            this.document.documentElement.style.setProperty('--theme-logo-url', `url(${assets.logo})`);
        }

        if (assets.logoWhite) {
            this.document.documentElement.style.setProperty('--theme-logo-white-url', `url(${assets.logoWhite})`);
        }

        if (assets.logoSmall) {
            this.document.documentElement.style.setProperty('--theme-logo-small-url', `url(${assets.logoSmall})`);
        }
    }

    /**
     * Actualiza el favicon
     */
    private updateFavicon(faviconUrl: string): void {
        let link = this.document.querySelector("link[rel*='icon']") as HTMLLinkElement;
        if (!link) {
            link = this.document.createElement('link');
            link.rel = 'icon';
            this.document.head.appendChild(link);
        }
        link.href = faviconUrl;
    }

    /**
     * Guarda el tema en localStorage
     */
    private saveThemeToStorage(theme: ThemeConfig): void {
        try {
            localStorage.setItem('cafler-current-theme', JSON.stringify({
                id: theme.id,
                name: theme.name,
                appliedAt: new Date().toISOString()
            }));
        } catch (error) {
            console.warn('No se pudo guardar el tema en localStorage:', error);
        }
    }

    /**
     * Carga el tema desde localStorage
     */
    loadThemeFromStorage(): { id: string; name: string; appliedAt: string; } | null {
        try {
            const saved = localStorage.getItem('cafler-current-theme');
            return saved ? JSON.parse(saved) : null;
        } catch (error) {
            console.warn('No se pudo cargar el tema desde localStorage:', error);
            return null;
        }
    }

    /**
     * Obtiene el tema actual
     */
    getCurrentTheme(): ThemeConfig | null {
        return this.currentThemeSubject.value;
    }

    /**
     * Aplica customizaciones específicas sobre el tema actual
     */
    applyCustomizations(customizations: Partial<ThemeConfig>): void {
        const currentTheme = this.getCurrentTheme();
        if (!currentTheme) return;

        const customizedTheme: ThemeConfig = {
            ...currentTheme,
            ...customizations,
            colors: { ...currentTheme.colors, ...customizations.colors },
            typography: { ...currentTheme.typography, ...customizations.typography },
            components: { ...currentTheme.components, ...customizations.components }
        };

        this.applyTheme(customizedTheme);
    }

    /**
     * Limpia todos los estilos aplicados
     */
    clearTheme(): void {
        if (this.themeStyleElement) {
            this.themeStyleElement.textContent = '';
        }
        if (this.customCSSElement) {
            this.customCSSElement.textContent = '';
        }
        this.currentThemeSubject.next(null);
    }

    /**
     * Obtiene una variable CSS específica
     */
    getCSSVariable(variableName: string): string {
        return getComputedStyle(this.document.documentElement).getPropertyValue(variableName);
    }

    /**
     * Establece una variable CSS específica
     */
    setCSSVariable(variableName: string, value: string): void {
        this.document.documentElement.style.setProperty(variableName, value);
    }
}
